import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toPng } from "html-to-image";
import { Share2, Loader2, Package } from "lucide-react";
import JSZip from "jszip";
import { BRAND_ACRONYM, BRAND_DOMAIN, BRAND_LOGO } from "../lib/brand";
import { toast } from "sonner";
import { ClubCrest } from "./ClubCrest";

export const GOLD = "#E5B842";
const slug = (s) => (s || "wsbf").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const today = () => new Date().toLocaleDateString("el-GR", { day: "2-digit", month: "long", year: "numeric" });
const FORM_C = { W: ["#064e3b", "#6ee7b7"], D: ["#334155", "#cbd5e1"], L: ["#4c0519", "#fda4af"] };

// Branded 1080px frame shared by every share PNG.
function ShareFrame({ tag, children }) {
  return (
    <div style={{ width: 1080, padding: 64, background: "radial-gradient(120% 80% at 0% 0%, #2a2208 0%, #0a0a0a 45%, #050505 100%)", color: "#e2e8f0", fontFamily: "inherit" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `2px solid ${GOLD}55`, paddingBottom: 28 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <img src={BRAND_LOGO} alt={BRAND_ACRONYM} style={{ height: 56, objectFit: "contain" }} />
          <div className="font-mono" style={{ fontSize: 14, letterSpacing: 4, color: "#94a3b8", textTransform: "uppercase", borderLeft: `1px solid ${GOLD}44`, paddingLeft: 20 }}>World Sports<br />Trading Federation</div>
        </div>
        <div className="font-mono" style={{ fontSize: 14, letterSpacing: 4, color: GOLD, textTransform: "uppercase" }}>{tag}</div>
      </div>
      {children}
      <div className="font-mono" style={{ display: "flex", justifyContent: "space-between", marginTop: 40, paddingTop: 22, borderTop: `1px solid ${GOLD}33`, fontSize: 15, letterSpacing: 2, color: "#64748b", textTransform: "uppercase" }}>
        <span>Generated {today()}</span><span style={{ color: GOLD }}>{BRAND_DOMAIN}</span>
      </div>
    </div>
  );
}

function CompTitle({ comp, sub }) {
  return (
    <div style={{ marginTop: 36 }}>
      <div className="font-heading" style={{ fontSize: 52, fontWeight: 900, textTransform: "uppercase", color: "#f8fafc", lineHeight: 1.05 }}>{comp.name}</div>
      <div className="font-mono" style={{ fontSize: 18, letterSpacing: 3, color: "#94a3b8", textTransform: "uppercase", marginTop: 10 }}>{sub}</div>
    </div>
  );
}

const capture = (node) => toPng(node, { pixelRatio: 1, cacheBust: true, backgroundColor: "#050505" });
const downloadUrl = (href, name) => { const a = document.createElement("a"); a.href = href; a.download = name; a.click(); };

// "Σάβ 24 Οκτ · 18:30" for date+time, "Σάβ 24 Οκτ" for date-only, "TBA" when unset.
export function fmtKickoff(v) {
  if (!v) return "TBA";
  const [d, t] = v.split("T");
  const [y, m, day] = d.split("-").map(Number);
  const date = new Date(y, m - 1, day).toLocaleDateString("el-GR", { weekday: "short", day: "numeric", month: "short" });
  return t ? `${date} · ${t.slice(0, 5)}` : date;
}

// Generic: renders `card` off-screen, captures it as PNG, shares (mobile) or downloads.
export function ShareButton({ testid, fileName, title, label = "Share", iconOnly = false, card, disabled = false, disabledTitle }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const share = async () => {
    setBusy(true);
    try {
      const dataUrl = await capture(ref.current);
      const name = `${slug(fileName)}.png`;
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], name, { type: "image/png" });
      const mobile = window.matchMedia("(max-width: 768px)").matches;
      if (mobile && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title }).catch(() => {});
      } else {
        downloadUrl(dataUrl, name);
        toast.success("Image downloaded");
      }
    } catch (e) { toast.error("Could not generate image"); } finally { setBusy(false); }
  };
  const Icon = busy ? Loader2 : Share2;
  return (
    <>
      <button onClick={share} disabled={busy || disabled} data-testid={testid} title={disabled ? disabledTitle : title} aria-label={title}
        className={`shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 rounded-md text-[10px] font-mono font-bold uppercase tracking-wider border border-[#E5B842]/40 text-[#E5B842] hover:bg-[#E5B842] hover:text-black disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#E5B842] ${iconOnly ? "p-1.5" : "px-2.5 py-1"}`}
        style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}>
        <Icon className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />{!iconOnly && label}
      </button>
      {!disabled && createPortal(
        <div aria-hidden style={{ position: "fixed", left: -20000, top: 0, pointerEvents: "none" }}>
          <div ref={ref}>{card}</div>
        </div>, document.body)}
    </>
  );
}

function FormChips({ form, size = 24, justify = "flex-start" }) {
  const slots = [...Array(Math.max(0, 5 - (form || []).length)).fill(null), ...(form || [])];
  return (
    <div style={{ display: "flex", gap: 6, marginTop: 6, justifyContent: justify }}>
      {slots.map((r, i) => (
        <span key={i} className="font-mono" style={{ width: size, height: size, borderRadius: 5, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: Math.round(size * 0.54), fontWeight: 800, background: r ? FORM_C[r][0] : "#141414", color: r ? FORM_C[r][1] : "#334155", border: `1px solid ${r ? FORM_C[r][1] + "55" : "#1f1f1f"}` }}>{r || "·"}</span>
      ))}
    </div>
  );
}

function StandingsCard({ comp, viewerClubId }) {
  const cols = ["P", "W", "D", "L", "GF:GA", "GD", "Pts"];
  return (
    <ShareFrame tag="ΒΑΘΜΟΛΟΓΙΑ · Standings">
      <CompTitle comp={comp} sub={[comp.format, comp.season_label, comp.status].filter(Boolean).join(" · ")} />
      <table data-testid="standings-share-card" style={{ width: "100%", marginTop: 36, borderCollapse: "collapse", fontSize: 26 }}>
        <thead><tr className="font-mono" style={{ fontSize: 16, letterSpacing: 2, color: "#64748b", textTransform: "uppercase", background: "#0d0d0d" }}>
          <th style={{ textAlign: "left", padding: "14px 18px", width: 60 }}>#</th>
          <th style={{ textAlign: "left", padding: "14px 10px" }}>Club · Form</th>
          {cols.map((c) => <th key={c} style={{ textAlign: "center", padding: "14px 8px", width: c === "GF:GA" ? 110 : 76, color: c === "Pts" ? GOLD : undefined }}>{c}</th>)}
        </tr></thead>
        <tbody>
          {comp.standings.map((r) => {
            const me = r.club_id === viewerClubId;
            const gdColor = r.GD > 0 ? "#6ee7b7" : r.GD < 0 ? "#fda4af" : "#94a3b8";
            return (
              <tr key={r.club_id} style={{ borderTop: "1px solid #2b2512", background: me ? "rgba(229,184,66,0.14)" : "transparent", boxShadow: me ? `inset 6px 0 0 ${GOLD}` : "none" }}>
                <td className="font-heading" style={{ padding: "18px", fontWeight: 900, color: me ? GOLD : "#94a3b8" }}>{r.rank}</td>
                <td style={{ padding: "14px 10px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 16, color: me ? GOLD : "#f1f5f9", fontWeight: 700 }}>
                    <ClubCrest club={r} size={44} />
                    <div><div>{r.name || r.short_name}</div><FormChips form={r.form} /></div>
                  </div>
                </td>
                {[[r.P, "#cbd5e1"], [r.W, "#6ee7b7"], [r.D, "#cbd5e1"], [r.L, "#fda4af"], [`${r.GF}:${r.GA}`, "#94a3b8"], [r.GD > 0 ? `+${r.GD}` : r.GD, gdColor]].map(([v, c], i) => (
                  <td key={i} className="font-mono" style={{ textAlign: "center", padding: "18px 8px", color: c, fontVariantNumeric: "tabular-nums" }}>{v}</td>
                ))}
                <td className="font-heading" style={{ textAlign: "center", padding: "18px 8px", fontWeight: 900, color: GOLD, fontSize: 30 }}>{r.Pts}</td>
              </tr>);
          })}
        </tbody>
      </table>
    </ShareFrame>
  );
}

export function StandingsShareButton({ comp, viewerClubId }) {
  return (
    <ShareButton testid={`standings-share-${comp.id}`} fileName={`wstf-standings-${comp.name}`}
      title={`${comp.name} — Standings`}
      label={<><span className="hidden sm:inline">Κοινοποίηση /</span> Share</>}
      card={<StandingsCard comp={comp} viewerClubId={viewerClubId} />} />
  );
}

function SideBlock({ club, align }) {
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 22, textAlign: "center" }}>
      <ClubCrest club={club} size={180} />
      <div className="font-heading" style={{ fontSize: 34, fontWeight: 900, textTransform: "uppercase", color: "#f8fafc", lineHeight: 1.1 }}>{club.name || club.short_name}</div>
      <div className="font-mono" style={{ fontSize: 15, letterSpacing: 4, color: GOLD, textTransform: "uppercase" }}>{align}</div>
    </div>
  );
}

function FixtureCard({ comp, f, home, away }) {
  const played = f.status === "PLAYED" && f.home_score != null;
  const date = f.scheduled_date ? new Date(f.scheduled_date).toLocaleDateString("el-GR", { day: "2-digit", month: "long", year: "numeric" }) : null;
  return (
    <ShareFrame tag={played ? "ΑΠΟΤΕΛΕΣΜΑ · Result" : "ΑΓΩΝΑΣ · Fixture"}>
      <CompTitle comp={comp} sub={[`Matchday ${f.matchday ?? "—"}`, comp.season_label, date].filter(Boolean).join(" · ")} />
      <div data-testid="fixture-share-card" style={{ display: "flex", alignItems: "center", marginTop: 56, padding: "48px 24px", background: "rgba(229,184,66,0.05)", border: `1px solid ${GOLD}33`, borderRadius: 18 }}>
        <SideBlock club={home} align="Home · Γηπεδούχος" />
        <div style={{ width: 300, textAlign: "center" }}>
          <div className="font-heading" style={{ fontSize: played ? 132 : 110, fontWeight: 900, color: GOLD, lineHeight: 1, fontVariantNumeric: "tabular-nums", textShadow: "0 6px 30px rgba(229,184,66,0.25)" }}>
            {played ? `${f.home_score}–${f.away_score}` : "VS"}
          </div>
          <div className="font-mono" style={{ marginTop: 18, fontSize: 15, letterSpacing: 4, color: "#94a3b8", textTransform: "uppercase" }}>{played ? "Full time" : f.status}</div>
        </div>
        <SideBlock club={away} align="Away · Φιλοξενούμενος" />
      </div>
    </ShareFrame>
  );
}

export function FixtureShareButton({ comp, f, us }) {
  const [home, away] = f.is_home ? [us, f.opponent] : [f.opponent, us];
  const tag = `${home.short_name || home.name}-${away.short_name || away.name}`;
  return (
    <ShareButton testid={`fixture-share-${f.id}`} iconOnly fileName={`wstf-md${f.matchday}-${tag}`}
      title={`${home.name} vs ${away.name} — ${comp.name}`}
      card={<FixtureCard comp={comp} f={f} home={home} away={away} />} />
  );
}

// ---------------------------------------------------------------- Matchday results
function MdRow({ f, compact }) {
  const played = f.status === "PLAYED" && f.home_score != null;
  const cs = compact ? 52 : 64;
  const name = (c) => (compact ? c.short_name || c.name : c.name || c.short_name);
  return (
    <div style={{ minWidth: 0, overflow: "hidden", display: "flex", alignItems: "center", gap: 16, padding: compact ? "18px 20px" : "22px 28px", background: "rgba(229,184,66,0.05)", border: `1px solid ${GOLD}2a`, borderRadius: 14 }}>
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 14, textAlign: "right", minWidth: 0 }}>
        <span className="font-heading" style={{ minWidth: 0, fontSize: compact ? 20 : 24, fontWeight: 800, color: "#f1f5f9", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: compact ? "nowrap" : "normal", lineHeight: 1.1 }}>{name(f.home)}</span>
        <ClubCrest club={f.home} size={cs} />
      </div>
      <div className="font-heading" style={{ flexShrink: 0, width: compact ? 110 : 150, textAlign: "center", fontSize: compact ? 36 : 48, fontWeight: 900, color: GOLD, fontVariantNumeric: "tabular-nums" }}>
        {played ? `${f.home_score}–${f.away_score}` : "VS"}
      </div>
      <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
        <ClubCrest club={f.away} size={cs} />
        <span className="font-heading" style={{ minWidth: 0, fontSize: compact ? 20 : 24, fontWeight: 800, color: "#f1f5f9", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: compact ? "nowrap" : "normal", lineHeight: 1.1 }}>{name(f.away)}</span>
      </div>
    </div>
  );
}

function MatchdayCard({ comp, md, fixtures }) {
  const two = fixtures.length > 4;
  return (
    <ShareFrame tag={`ΑΓΩΝΙΣΤΙΚΗ ${md} · Matchday ${md}`}>
      <CompTitle comp={comp} sub={`ΑΓΩΝΙΣΤΙΚΗ ${md} / MATCHDAY ${md} · ${comp.season_label || ""}`} />
      <div data-testid="matchday-share-card" style={{ marginTop: 40, display: "grid", gridTemplateColumns: two ? "1fr 1fr" : "1fr", gap: 16 }}>
        {fixtures.map((f) => <MdRow key={f.id} f={f} compact={two} />)}
      </div>
    </ShareFrame>
  );
}

function PreviewRow({ f, formMap, compact }) {
  const played = f.status === "PLAYED" && f.home_score != null;
  const cs = compact ? 48 : 60;
  const name = (c) => (compact ? c.short_name || c.name : c.name || c.short_name);
  const when = fmtKickoff(f.scheduled_date);
  const side = (c, right) => (
    <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 14, flexDirection: right ? "row-reverse" : "row", textAlign: right ? "right" : "left" }}>
      <ClubCrest club={c} size={cs} />
      <div style={{ minWidth: 0 }}>
        <div className="font-heading" style={{ fontSize: compact ? 20 : 24, fontWeight: 800, color: "#f1f5f9", lineHeight: 1.1, whiteSpace: compact ? "nowrap" : "normal", overflow: "hidden", textOverflow: "ellipsis" }}>{name(c)}</div>
        <FormChips form={formMap[c.id]} size={compact ? 18 : 22} justify={right ? "flex-end" : "flex-start"} />
      </div>
    </div>
  );
  return (
    <div style={{ minWidth: 0, overflow: "hidden", display: "flex", alignItems: "center", gap: 14, padding: compact ? "16px 18px" : "20px 26px", background: "rgba(229,184,66,0.05)", border: `1px solid ${GOLD}2a`, borderRadius: 14 }}>
      {side(f.home, true)}
      <div style={{ flexShrink: 0, width: compact ? 100 : 140, textAlign: "center" }}>
        <div className="font-heading" style={{ fontSize: compact ? 34 : 44, fontWeight: 900, color: GOLD, fontVariantNumeric: "tabular-nums" }}>{played ? `${f.home_score}–${f.away_score}` : "VS"}</div>
        {!played && <div className="font-mono" data-kickoff={when} style={{ fontSize: 13, letterSpacing: 1, color: "#cbd5e1", marginTop: 6, whiteSpace: "nowrap" }}>{when}</div>}
      </div>
      {side(f.away, false)}
    </div>
  );
}

function MatchdayPreviewCard({ comp, md, fixtures, standings }) {
  const two = fixtures.length > 4;
  const formMap = Object.fromEntries((standings || []).map((r) => [r.club_id, r.form || []]));
  return (
    <ShareFrame tag={`ΕΡΧΕΤΑΙ · Coming up`}>
      <CompTitle comp={comp} sub={`ΕΡΧΕΤΑΙ / COMING UP — ΑΓΩΝΙΣΤΙΚΗ ${md} · ${comp.season_label || ""}`} />
      <div data-testid="matchday-preview-card" style={{ marginTop: 40, display: "grid", gridTemplateColumns: two ? "1fr 1fr" : "1fr", gap: 16 }}>
        {fixtures.map((f) => <PreviewRow key={f.id} f={f} formMap={formMap} compact={two} />)}
      </div>
      <div className="font-mono" style={{ marginTop: 18, fontSize: 13, letterSpacing: 3, color: "#64748b", textTransform: "uppercase" }}>Φόρμα · Form — last 5, oldest → newest</div>
    </ShareFrame>
  );
}

// Matchday picker + share button; `fixtures` = ALL fixtures of the competition.
// Upcoming matchdays (any non-PLAYED fixture) render as Preview, otherwise Results.
export function MatchdayShareBar({ comp, fixtures, standings, testid = "matchday-share" }) {
  const mds = [...new Set((fixtures || []).map((f) => f.matchday))].filter((m) => m != null).sort((a, b) => a - b);
  const [md, setMd] = useState(null);
  const cur = md ?? mds[0];
  if (!mds.length) return null;
  const list = fixtures.filter((f) => f.matchday === cur);
  const upcoming = list.some((f) => f.status !== "PLAYED");
  return (
    <div className="inline-flex items-center gap-1.5" data-testid={`${testid}-bar`} data-variant={upcoming ? "preview" : "results"}>
      <select value={cur} onChange={(e) => setMd(Number(e.target.value))} data-testid={`${testid}-select`}
        className="bg-[#0a0a0a] border border-[#E5B842]/30 rounded-md px-1.5 py-1 text-[10px] font-mono uppercase text-slate-200 focus:outline-none">
        {mds.map((m) => <option key={m} value={m}>{`MD ${m}`}</option>)}
      </select>
      {upcoming ? (
        <ShareButton testid={`${testid}-btn`} fileName={`wstf-${comp.name}-matchday-${cur}-preview`}
          title={`${comp.name} — Matchday ${cur} preview`}
          label={<><span className="hidden sm:inline">Προεπισκόπηση /</span> Preview</>}
          card={<MatchdayPreviewCard comp={comp} md={cur} fixtures={list} standings={standings} />} />
      ) : (
        <ShareButton testid={`${testid}-btn`} fileName={`wstf-${comp.name}-matchday-${cur}`}
          title={`${comp.name} — Matchday ${cur} results`}
          label={<><span className="hidden sm:inline">Αποτελέσματα /</span> Results</>}
          card={<MatchdayCard comp={comp} md={cur} fixtures={list} />} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------- League leaders
const byThen = (key, dir) => (a, b) => dir * (a[key] - b[key]) || b.Pts - a.Pts || (a.name || "").localeCompare(b.name || "");

export function computeLeaders(standings) {
  const rows = (standings || []).filter((r) => r.P > 0);
  if (!rows.length) return [];
  const pick = (cmp) => [...rows].sort(cmp)[0];
  const atk = pick(byThen("GF", -1));
  const def = pick(byThen("GA", 1));
  const run = pick(byThen("unbeaten_run", -1));
  const out = [
    { key: "attack", title: "ΚΟΡΥΦΑΙΑ ΕΠΙΘΕΣΗ", sub: "Top attack", club: atk, value: atk.GF, unit: "GF" },
    { key: "defence", title: "ΚΑΛΥΤΕΡΗ ΑΜΥΝΑ", sub: "Best defence", club: def, value: def.GA, unit: "GA" },
  ];
  if ((run.unbeaten_run || 0) > 0) out.push({ key: "unbeaten", title: "ΜΕΓΑΛΥΤΕΡΟ ΑΗΤΤΗΤΟ", sub: "Longest unbeaten run", club: run, value: run.unbeaten_run, unit: run.unbeaten_run === 1 ? "match" : "matches" });
  return out;
}

function LeadersCard({ comp, leaders }) {
  return (
    <ShareFrame tag="ΚΟΡΥΦΑΙΟΙ · League leaders">
      <CompTitle comp={comp} sub={[comp.season_label, comp.status].filter(Boolean).join(" · ")} />
      <div data-testid="leaders-share-card" style={{ marginTop: 40, display: "flex", flexDirection: "column", gap: 18 }}>
        {leaders.map((l) => (
          <div key={l.key} style={{ display: "flex", alignItems: "center", gap: 28, padding: "26px 32px", background: "rgba(229,184,66,0.05)", border: `1px solid ${GOLD}33`, borderRadius: 16 }}>
            <ClubCrest club={l.club} size={96} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="font-mono" style={{ fontSize: 15, letterSpacing: 4, color: GOLD }}>{l.title} · {l.sub.toUpperCase()}</div>
              <div className="font-heading" style={{ fontSize: 40, fontWeight: 900, color: "#f8fafc", textTransform: "uppercase", marginTop: 6, lineHeight: 1.05 }}>{l.club.name}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div className="font-heading" style={{ fontSize: 80, fontWeight: 900, color: GOLD, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{l.value}</div>
              <div className="font-mono" style={{ fontSize: 15, letterSpacing: 3, color: "#94a3b8", textTransform: "uppercase", marginTop: 6 }}>{l.unit}</div>
            </div>
          </div>
        ))}
      </div>
    </ShareFrame>
  );
}

export function LeadersShareButton({ comp, standings, iconOnly = false, testid }) {
  const leaders = computeLeaders(standings);
  return (
    <ShareButton testid={testid || `leaders-share-${comp.id}`} iconOnly={iconOnly} fileName={`wstf-leaders-${comp.name}`}
      title={`${comp.name} — League leaders`} disabled={!leaders.length}
      disabledTitle="Δεν υπάρχουν αποτελέσματα ακόμα · No played fixtures yet"
      label={<><span className="hidden sm:inline">Κορυφαίοι /</span> Leaders</>}
      card={leaders.length ? <LeadersCard comp={comp} leaders={leaders} /> : null} />
  );
}

// ---------------------------------------------------------------- Club season card
const RES_C = { W: ["#064e3b", "#6ee7b7"], D: ["#334155", "#cbd5e1"], L: ["#4c0519", "#fda4af"] };

function StatChip({ k, v, color = "#f1f5f9" }) {
  return (
    <div style={{ flex: 1, padding: "16px 8px", textAlign: "center", background: "#0d0d0d", border: `1px solid ${GOLD}2a`, borderRadius: 12 }}>
      <div className="font-mono" style={{ fontSize: 14, letterSpacing: 3, color: "#64748b" }}>{k}</div>
      <div className="font-heading" style={{ fontSize: 38, fontWeight: 900, color, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>{v}</div>
    </div>
  );
}

function SeasonCard({ comp, us, fixtures }) {
  const gd = us.GD > 0 ? `+${us.GD}` : us.GD;
  return (
    <ShareFrame tag="ΚΑΡΤΑ ΣΕΖΟΝ · Season card">
      <div data-testid="season-share-card" style={{ display: "flex", alignItems: "center", gap: 36, marginTop: 40 }}>
        <ClubCrest club={us} size={170} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="font-heading" style={{ fontSize: 50, fontWeight: 900, textTransform: "uppercase", color: "#f8fafc", lineHeight: 1.05 }}>{us.name}</div>
          <div className="font-mono" style={{ fontSize: 18, letterSpacing: 3, color: "#94a3b8", textTransform: "uppercase", marginTop: 10 }}>{comp.name} · {comp.season_label}</div>
          <div style={{ marginTop: 14 }}><FormChips form={us.form} /></div>
        </div>
        <div style={{ textAlign: "center", padding: "18px 26px", border: `2px solid ${GOLD}`, borderRadius: 16, background: "rgba(229,184,66,0.1)" }}>
          <div className="font-mono" style={{ fontSize: 15, letterSpacing: 4, color: GOLD }}>ΘΕΣΗ</div>
          <div className="font-heading" style={{ fontSize: 64, fontWeight: 900, color: GOLD, lineHeight: 1 }}>{us.rank}<span className="font-mono" style={{ fontSize: 34, fontWeight: 700, textTransform: "none", verticalAlign: "super" }}>η</span></div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 12, marginTop: 36 }}>
        <StatChip k="P" v={us.P} />
        <StatChip k="W" v={us.W} color="#6ee7b7" />
        <StatChip k="D" v={us.D} color="#cbd5e1" />
        <StatChip k="L" v={us.L} color="#fda4af" />
        <StatChip k="GF:GA" v={`${us.GF}:${us.GA}`} color="#94a3b8" />
        <StatChip k="GD" v={gd} color={us.GD > 0 ? "#6ee7b7" : us.GD < 0 ? "#fda4af" : "#94a3b8"} />
        <StatChip k="PTS" v={us.Pts} color={GOLD} />
      </div>
      <div className="font-mono" style={{ marginTop: 34, fontSize: 14, letterSpacing: 4, color: GOLD, textTransform: "uppercase" }}>Αποτελέσματα · Results</div>
      <div style={{ marginTop: 12 }}>
        {fixtures.map((f) => {
          const rc = f.result ? RES_C[f.result] : null;
          return (
            <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 18, padding: "12px 6px", borderTop: "1px solid #2b2512", fontSize: 24 }}>
              <span className="font-mono" style={{ width: 70, color: "#64748b", fontSize: 16 }}>MD {f.matchday}</span>
              <ClubCrest club={f.opponent} size={36} />
              <span style={{ flex: 1, fontWeight: 700, color: "#f1f5f9" }}>vs {f.opponent.name}</span>
              <span className="font-mono" style={{ width: 40, color: "#94a3b8", fontSize: 18, textAlign: "center" }}>{f.venue}</span>
              <span className="font-heading" style={{ width: 100, textAlign: "center", fontWeight: 900, color: GOLD }}>{f.status === "PLAYED" ? `${f.our_score}–${f.their_score}` : "—"}</span>
              <span className="font-mono" style={{ width: 36, height: 36, borderRadius: 8, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 17, fontWeight: 800, background: rc ? rc[0] : "#141414", color: rc ? rc[1] : "#475569" }}>{f.result || "·"}</span>
            </div>);
        })}
      </div>
    </ShareFrame>
  );
}

export function SeasonShareButton({ comp, us, fixtures }) {
  if (!us || !us.club_id) return null;
  return (
    <ShareButton testid={`season-share-${comp.id}`} fileName={`wstf-season-${us.short_name || us.name}-${comp.name}`}
      title={`${us.name} — ${comp.name} season`}
      label={<><span className="hidden sm:inline">Κάρτα σεζόν /</span> Season</>}
      card={<SeasonCard comp={comp} us={us} fixtures={fixtures} />} />
  );
}

// ---------------------------------------------------------------- Weekly social pack
// Next matchday with any SCHEDULED fixture → preview; else last played matchday → results.
export function pickPackMatchday(fixtures) {
  const mds = [...new Set((fixtures || []).map((f) => f.matchday))].filter((m) => m != null).sort((a, b) => a - b);
  const of = (m) => fixtures.filter((f) => f.matchday === m);
  const up = mds.find((m) => of(m).some((f) => f.status === "SCHEDULED"));
  if (up != null) return { kind: "preview", md: up, list: of(up) };
  const played = [...mds].reverse().find((m) => of(m).some((f) => f.status === "PLAYED"));
  return played != null ? { kind: "results", md: played, list: of(played) } : null;
}

// Ready-to-post EL + EN caption from real data (last played matchday + top-3).
export function buildCaption(comp, fixtures, standings) {
  const mds = [...new Set((fixtures || []).filter((f) => f.status === "PLAYED").map((f) => f.matchday))].sort((a, b) => a - b);
  const md = mds[mds.length - 1];
  const res = (fixtures || []).filter((f) => f.matchday === md && f.status === "PLAYED")
    .map((f) => `${f.home.name} ${f.home_score}-${f.away_score} ${f.away.name}`);
  const top = (standings || []).slice(0, 3);
  const tag = `#${(comp.name || "").replace(/[^A-Za-z0-9]/g, "")}`;
  const nxt = pickPackMatchday(fixtures);
  const el = [`🏆 ${comp.name} — ${comp.season_label || ""}`.trim(), ""];
  const en = [`🏆 ${comp.name} — ${comp.season_label || ""}`.trim(), ""];
  if (md != null) {
    el.push(`Αποτελέσματα ${md}ης αγωνιστικής:`, ...res.map((r) => `• ${r}`), "");
    en.push(`Matchday ${md} results:`, ...res.map((r) => `• ${r}`), "");
  }
  if (top.some((r) => r.P > 0)) {
    el.push("Βαθμολογία (Top 3):", ...top.map((r) => `${r.rank}. ${r.name} — ${r.Pts} βαθμοί`), "");
    en.push("Standings (Top 3):", ...top.map((r) => `${r.rank}. ${r.name} — ${r.Pts} pts`), "");
  }
  if (nxt && nxt.kind === "preview") {
    el.push(`Επόμενη: ${nxt.md}η αγωνιστική — ${nxt.list.map((f) => `${f.home.name} vs ${f.away.name} (${fmtKickoff(f.scheduled_date)})`).join(", ")}`, "");
    en.push(`Up next: Matchday ${nxt.md} — ${nxt.list.map((f) => `${f.home.name} vs ${f.away.name}`).join(", ")}`, "");
  }
  el.push(`Περισσότερα στο ${BRAND_DOMAIN} ⚽`, `#${BRAND_ACRONYM} ${tag}`);
  en.push(`More at ${BRAND_DOMAIN} ⚽`, `#${BRAND_ACRONYM} ${tag}`);
  return ["— ΕΛΛΗΝΙΚΑ —", ...el, "", "— ENGLISH —", ...en, ""].join("\n");
}

export function WeeklyPackButton({ comp, fixtures, standings, viewerClubId, iconOnly = false, testid }) {
  const [busy, setBusy] = useState(false);
  const [render, setRender] = useState(false);
  const refs = useRef({});
  const leaders = computeLeaders(standings);
  const pick = pickPackMatchday(fixtures);
  const items = [
    { key: "standings", file: "01-standings", card: <StandingsCard comp={{ ...comp, standings }} viewerClubId={viewerClubId} /> },
    leaders.length > 0 && { key: "leaders", file: "02-leaders", card: <LeadersCard comp={comp} leaders={leaders} /> },
    pick && { key: "matchday", file: `03-matchday-${pick.md}-${pick.kind}`,
      card: pick.kind === "preview"
        ? <MatchdayPreviewCard comp={comp} md={pick.md} fixtures={pick.list} standings={standings} />
        : <MatchdayCard comp={comp} md={pick.md} fixtures={pick.list} /> },
  ].filter(Boolean);
  useEffect(() => {
    if (!render) return;
    (async () => {
      const base = `wstf-${slug(comp.name)}-weekly-pack-${new Date().toISOString().slice(0, 10)}`;
      const shots = [];
      try {
        const imgs = items.flatMap((it) => [...(refs.current[it.key]?.querySelectorAll("img") || [])]);
        await Promise.all(imgs.map((im) => (im.complete && im.naturalWidth ? im.decode().catch(() => {}) : new Promise((r) => { im.onload = r; im.onerror = r; }))));
        for (const it of items) shots.push({ name: `${base}-${it.file}.png`, url: await capture(refs.current[it.key]) });
        try {
          const zip = new JSZip();
          shots.forEach((s) => zip.file(s.name, s.url.split(",")[1], { base64: true }));
          zip.file("caption.txt", buildCaption(comp, fixtures, standings));
          const blob = await zip.generateAsync({ type: "blob" });
          const href = URL.createObjectURL(blob);
          downloadUrl(href, `${base}.zip`);
          setTimeout(() => URL.revokeObjectURL(href), 4000);
        } catch (e) {
          shots.forEach((s, i) => setTimeout(() => downloadUrl(s.url, s.name), i * 400));
        }
        const missing = [!leaders.length && "leaders (no played fixtures yet)", !pick && "matchday (no fixtures)"].filter(Boolean);
        toast.success(`Weekly pack: ${shots.length} images`);
        if (missing.length) toast.info(`Omitted: ${missing.join(", ")}`);
      } catch (e) { toast.error("Could not generate weekly pack"); } finally { setRender(false); setBusy(false); }
    })();
  }, [render]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <button onClick={() => { setBusy(true); setRender(true); }} disabled={busy} data-testid={testid || `weekly-pack-${comp.id}`}
        title={`${comp.name} — Weekly social pack (.zip)`} aria-label="Weekly social pack" data-pack={items.map((i) => i.file).join(",")}
        className={`shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 rounded-md text-[10px] font-mono font-bold uppercase tracking-wider bg-[#E5B842] text-black hover:bg-[#C99E30] disabled:opacity-50 ${iconOnly ? "p-1.5" : "px-2.5 py-1"}`}
        style={{ transition: "background-color 0.15s ease" }}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Package className="w-3 h-3" />}
        {!iconOnly && <><span className="hidden sm:inline">Εβδομαδιαίο πακέτο /</span> Weekly pack</>}
      </button>
      {render && createPortal(
        <div aria-hidden style={{ position: "fixed", left: -20000, top: 0, pointerEvents: "none" }}>
          {items.map((it) => <div key={it.key} ref={(el) => { refs.current[it.key] = el; }}>{it.card}</div>)}
        </div>, document.body)}
    </>
  );
}
